/**
 * CR305+CR310: Recipe Basics section (name, scope, provider, description, template toggle)
 */

import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Save, LayoutTemplate } from "lucide-react";
import type { Recipe } from "@/lib/recipes-studio/types";

interface RecipeBasicsSectionProps {
  recipe: Recipe;
  saving: boolean;
  onSave: (updates: Partial<Recipe>) => void;
}

export function RecipeBasicsSection({ recipe, saving, onSave }: RecipeBasicsSectionProps) {
  const [name, setName] = useState(recipe.recipe_name);
  const [description, setDescription] = useState(recipe.description ?? "");
  const [isTemplate, setIsTemplate] = useState(recipe.is_template ?? false);

  // Reset local state when recipe changes
  useEffect(() => {
    setName(recipe.recipe_name);
    setDescription(recipe.description ?? "");
    setIsTemplate(recipe.is_template ?? false);
  }, [recipe.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const hasChanges =
    name !== recipe.recipe_name ||
    description !== (recipe.description ?? "") ||
    isTemplate !== (recipe.is_template ?? false);

  const handleSave = () => {
    onSave({
      recipe_name: name,
      description: description || null,
      is_template: isTemplate,
    });
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-sm font-medium">Basics</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-1.5">
          <Label className="text-xs">Recipe Name</Label>
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="My Web Crawl Recipe"
          />
        </div>

        <div className="space-y-1.5">
          <Label className="text-xs">Description</Label>
          <Input
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Optional description..."
          />
        </div>

        <div className="flex items-center gap-3">
          <div className="space-y-1">
            <Label className="text-xs text-muted-foreground">Scope</Label>
            <div>
              <Badge variant="outline">{recipe.recipe_scope}</Badge>
            </div>
          </div>
          <div className="space-y-1">
            <Label className="text-xs text-muted-foreground">Provider</Label>
            <div>
              <Badge variant="secondary">{recipe.provider}</Badge>
            </div>
          </div>
        </div>

        {/* CR310: Template toggle */}
        <div className="flex items-start gap-3 pt-2 border-t border-border/50">
          <Checkbox
            id="is-template"
            checked={isTemplate}
            onCheckedChange={(checked) => setIsTemplate(checked === true)}
          />
          <div className="space-y-0.5">
            <Label htmlFor="is-template" className="text-xs font-medium flex items-center gap-1.5 cursor-pointer">
              <LayoutTemplate className="h-3 w-3" />
              Template
            </Label>
            <p className="text-xs text-muted-foreground">
              Mark as a reusable template. Templates can be used as starting points when creating new recipes.
            </p>
          </div>
        </div>

        {hasChanges && (
          <Button size="sm" onClick={handleSave} disabled={saving || !name.trim()}>
            <Save className="h-3.5 w-3.5 mr-1.5" />
            {saving ? "Saving..." : "Save"}
          </Button>
        )}
      </CardContent>
    </Card>
  );
}
